'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Drawer, Grid, Heading, Icon, Row, SelectField, TextField, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useCallback, useEffect, useId, useState } from 'react';
import { useCustomAddressStore, zipCodeSetter } from '@libs/common-next';
import { ErrorMessage } from './error-message';
import { localized } from './sdd-localize';
import { StreetAddressInput } from './street-address-input';
import { useCustomAddressDrawerStore } from './use-custom-address-drawer-store';
import { useSddAddressForm } from './use-sdd-address-form';
import './SddAddressDrawer.scss';
export type SddAddressDrawerProps = Readonly<{
  showBackButton?: boolean;
  /** When the host drawer is closed, the stacked SDD address drawer should close too. */
  forceClosed?: boolean;
  /** Reports the address validation error state to an optional host. */
  onErrorChange?: (hasError: boolean) => void;
  onOpenChange?: (isOpen: boolean) => void;
  /** Sparky close (X / overlay / Esc). Used when stacked over another drawer. */
  onCloseAll?: () => void;
  /** Mini PDP shows Add address / Change in delivery details. PDP keeps its own trigger there. */
  showTrigger?: boolean;
  /** Controlled open state for stacked Mini PDP. Uncontrolled stacked mode uses internal state. */
  isOpen?: boolean;
  isDeliveryAvailable: boolean;
  isLoading: boolean;
  isError: boolean;
}>;
export const SddAddressDrawer = t0 => {
  const $ = _c(58);
  const {
    showBackButton: t1,
    forceClosed: t2,
    onErrorChange,
    onOpenChange,
    onCloseAll,
    showTrigger: t3,
    isOpen,
    isDeliveryAvailable,
    isLoading,
    isError
  } = t0;
  const showBackButton = t1 === undefined ? false : t1;
  const forceClosed = t2 === undefined ? false : t2;
  const showTrigger = t3 === undefined ? showBackButton : t3;
  const storeIsOpen = useCustomAddressDrawerStore(_temp);
  const closeStore = useCustomAddressDrawerStore(_temp2);
  const submittedStreet = useCustomAddressStore(_temp3);
  const [internalIsActive, setInternalIsActive] = useState(false);
  const headingId = useId();
  const [hasBeenRendered, setHasBeenRendered] = useState(false);
  const isStackedControlled = isOpen !== undefined;
  const isStackedOpen = isStackedControlled ? isOpen : internalIsActive;
  const isActive = showBackButton ? isStackedOpen : storeIsOpen;
  let t4;
  if ($[0] !== closeStore || $[1] !== isStackedControlled || $[2] !== onOpenChange || $[3] !== showBackButton) {
    t4 = () => {
      if (showBackButton) {
        if (!isStackedControlled) {
          setInternalIsActive(false);
        }
      } else {
        closeStore();
      }
      onOpenChange?.(false);
    };
    $[0] = closeStore;
    $[1] = isStackedControlled;
    $[2] = onOpenChange;
    $[3] = showBackButton;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const closeTopDrawer = t4;
  let t5;
  if ($[5] !== closeTopDrawer || $[6] !== isDeliveryAvailable || $[7] !== isError || $[8] !== isLoading) {
    t5 = {
      onSuccessAction: closeTopDrawer,
      isDeliveryAvailable,
      isLoading,
      isError
    };
    $[5] = closeTopDrawer;
    $[6] = isDeliveryAvailable;
    $[7] = isError;
    $[8] = isLoading;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    register,
    setValue,
    trigger,
    formState: t6,
    watch,
    onSubmit,
    handleAddressSelect,
    handleFormReset,
    isValidating,
    errorType,
    formKey,
    showComplement,
    toggleComplement
  } = useSddAddressForm(t5);
  const {
    errors
  } = t6;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setHasBeenRendered(true);
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== forceClosed || $[13] !== onOpenChange) {
    t9 = () => {
      if (forceClosed) {
        setInternalIsActive(false);
        onOpenChange?.(false);
      }
    };
    t10 = [forceClosed, onOpenChange];
    $[12] = forceClosed;
    $[13] = onOpenChange;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== errorType || $[17] !== isActive || $[18] !== onErrorChange) {
    t11 = () => {
      onErrorChange?.(isActive && errorType === "apiError");
    };
    t12 = [errorType, isActive, onErrorChange];
    $[16] = errorType;
    $[17] = isActive;
    $[18] = onErrorChange;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  if ($[21] !== handleFormReset) {
    t13 = () => {
      handleFormReset();
    };
    $[21] = handleFormReset;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== handleFormReset || $[24] !== isActive) {
    t14 = [handleFormReset, isActive];
    $[23] = handleFormReset;
    $[24] = isActive;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  if ($[26] !== closeTopDrawer) {
    t15 = event => {
      event.stopPropagation();
      event.preventDefault();
      closeTopDrawer();
    };
    $[26] = closeTopDrawer;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const handleBack = t15;
  let t16;
  if ($[28] !== closeTopDrawer || $[29] !== onCloseAll) {
    t16 = () => {
      closeTopDrawer();
      onCloseAll?.();
    };
    $[28] = closeTopDrawer;
    $[29] = onCloseAll;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const handleClose = t16;
  let t17;
  if ($[31] !== isStackedControlled || $[32] !== onOpenChange || $[33] !== showTrigger || $[34] !== submittedStreet) {
    t17 = showTrigger && <Button type="button" variant="link" className="sdd-address-drawer__trigger" text={submittedStreet ? "Change" : "Add address"} data-testid="sdd-address-drawer-trigger" onClick={() => {
      if (!isStackedControlled) {
        setInternalIsActive(true);
      }
      onOpenChange?.(true);
    }} />;
    $[31] = isStackedControlled;
    $[32] = onOpenChange;
    $[33] = showTrigger;
    $[34] = submittedStreet;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== errorType || $[37] !== errors || $[38] !== formKey || $[39] !== handleAddressSelect || $[40] !== handleBack || $[41] !== handleClose || $[42] !== hasBeenRendered || $[43] !== headingId || $[44] !== isActive || $[45] !== isValidating || $[46] !== onSubmit || $[47] !== register || $[48] !== setValue || $[49] !== showBackButton || $[50] !== showComplement || $[51] !== toggleComplement || $[52] !== trigger || $[53] !== watch) {
    t18 = hasBeenRendered && <Drawer align="right" isActive={isActive} onClose={handleClose} data-testid="sdd-drawer" className={cn("sdd-address-drawer", {
      "sdd-address-drawer--stacked": showBackButton
    })} ariaLabelledBy={headingId}><Drawer.Header className="sdd-address-drawer__header">{showBackButton && <button type="button" aria-label="Back" className="sdd-address-drawer__back" data-testid="sdd-address-drawer-back-btn" onClick={handleBack}><Icon name="chevron-left" size="lg" aria-hidden="true" /></button>}<Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">Same-day delivery</Heading></Drawer.Header><Drawer.Body key={formKey}>{errorType && <ErrorMessage errorType={errorType} />}<TextPassage className="check-availability">Please provide more details to confirm same-day delivery is available in your area</TextPassage><Row className="form-row sdd-form-input__street"><StreetAddressInput value={watch("streetLine1")} onAddressSelect={handleAddressSelect} isError={Boolean(errors.streetLine1)} fieldNote={errors.streetLine1?.message} {...register("streetLine1")} /></Row><Row className="form-row">{showComplement ? <TextField data-cs-mask={true} data-testid="sdd-complement" className="sdd-form-input" label="Apt/suite/other" type="text" {...register("streetLine2")} /> : <Button iconName="plus" data-testid="sdd-show-complement" text="Add apt/suite/other" variant="link" onClick={toggleComplement} />}</Row><Row className="form-row"><Grid variant="side-by-side" gap="sm" className="sdd-form-row-split"><Grid.Item><TextField data-cs-mask={true} data-testid="sdd-zip" className="sdd-form-input" label={localized.zipPostalCode} type="text" isError={Boolean(errors.zipPostalCode)} fieldNote={errors.zipPostalCode?.message} {...register("zipPostalCode", {
                onChange: _temp4,
                onBlur: () => {
                  trigger("zipPostalCode");
                }
              })} /></Grid.Item><Grid.Item><SelectField data-cs-mask={true} data-testid="sdd-state" className="sdd-form-input" label={localized.stateProvinceLabel} isError={Boolean(errors.stateProvince)} fieldNote={errors.stateProvince?.message} value={watch("stateProvince")} onChange={ev_0 => {
                const value_0 = (ev_0.target as HTMLSelectElement).value;
                setValue("stateProvince", value_0, {
                  shouldDirty: true,
                  shouldValidate: true
                });
                trigger("stateProvince");
              }} items={localized.stateProvinceSelection.map(_temp5)} /></Grid.Item></Grid></Row><Row className="form-row"><TextField data-cs-mask={true} data-testid="sdd-city" className="sdd-form-input" label="City" type="text" isError={Boolean(errors.city)} fieldNote={errors.city?.message} {...register("city")} /></Row><Row className="form-row"><Button fullWidth={true} text="Check address" variant="primary" type="button" data-testid="sdd-check-address" loading={isValidating} onClick={onSubmit} /></Row></Drawer.Body></Drawer>;
    $[36] = errorType;
    $[37] = errors;
    $[38] = formKey;
    $[39] = handleAddressSelect;
    $[40] = handleBack;
    $[41] = handleClose;
    $[42] = hasBeenRendered;
    $[43] = headingId;
    $[44] = isActive;
    $[45] = isValidating;
    $[46] = onSubmit;
    $[47] = register;
    $[48] = setValue;
    $[49] = showBackButton;
    $[50] = showComplement;
    $[51] = toggleComplement;
    $[52] = trigger;
    $[53] = watch;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t17 || $[56] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  return t19;
};
function _temp(state) {
  return state.isOpen;
}
function _temp2(state_0) {
  return state_0.close;
}
function _temp3(state_1) {
  return state_1.address?.streetLine1;
}
function _temp4(ev) {
  zipCodeSetter(ev, "zip", (_, value) => {
    (ev.target as HTMLInputElement).value = value;
  });
}
function _temp5(t0) {
  const {
    abbr,
    name
  } = t0;
  return {
    label: name,
    value: abbr,
    disabled: false
  };
}